import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  Tooltip,
  useTheme
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  StraightenOutlined as MeasureIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import unitService from '../../services/unitService';
import { getCurrentUser } from '../../services/authService';
import PageTitle from '../../components/PageTitle';
import { setPageTitle } from '../../utils/pageTitle';

/**
 * Units of Measurement management page component
 * @returns {JSX.Element} Units page
 */
const UnitsPage = () => {
  const theme = useTheme();
  const user = getCurrentUser();
  const companyId = user?.companyId || 1; // Fallback to 1 for development
  
  // Set page title on component mount
  useEffect(() => {
    setPageTitle("Units");
  }, []);
  
  // State management
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [unitToDelete, setUnitToDelete] = useState(null);
  const [formValues, setFormValues] = useState({
    name: '',
    abbreviation: '',
    description: ''
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  // Load units of measurement - wrapped in useCallback
  const loadUnits = useCallback(async () => {
    try {
      setLoading(true);
      const response = await unitService.getAllUnits({
        page,
        size: rowsPerPage,
        sort: 'name',
        direction: 'ASC'
      });
      
      // Add proper null checks before accessing properties
      if (response && response.content) {
        setUnits(response.content);
        setTotalElements(response.totalElements || 0);
      } else {
        // If response format is different, handle it appropriately
        // It might be an array directly instead of a Page object
        setUnits(Array.isArray(response) ? response : []);
        setTotalElements(Array.isArray(response) ? response.length : 0);
      }
    } catch (error) {
      console.error('Error loading units:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load units of measurement',
        severity: 'error'
      });
      // Set empty array to avoid further errors
      setUnits([]);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);
  
  // Initial load
  useEffect(() => {
    loadUnits();
  }, [loadUnits]); // Depend only on loadUnits which is now memoized
  
  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Dialog handlers
  const handleOpenDialog = (unit = null) => {
    if (unit) {
      setSelectedUnit(unit);
      setFormValues({
        name: unit.name,
        abbreviation: unit.abbreviation,
        description: unit.description || ''
      });
    } else {
      setSelectedUnit(null);
      setFormValues({
        name: '',
        abbreviation: '',
        description: ''
      });
    }
    setDialogOpen(true);
  };
  
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedUnit(null);
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value
    });
  };
  
  // Form submission
  const handleSubmit = async () => {
    try {
      const unitData = {
        name: formValues.name,
        abbreviation: formValues.abbreviation,
        description: formValues.description,
        companyId
      };
      
      if (selectedUnit) {
        // Update existing unit
        await unitService.updateUnit(selectedUnit.id, unitData);
        setSnackbar({
          open: true,
          message: 'Unit of measurement updated successfully',
          severity: 'success'
        });
      } else {
        // Create new unit
        await unitService.createUnit(unitData);
        setSnackbar({
          open: true,
          message: 'Unit of measurement created successfully',
          severity: 'success'
        });
      }
      
      // Close the dialog and refresh data
      handleCloseDialog();
      loadUnits();
    } catch (error) {
      console.error('Error saving unit:', error);
      setSnackbar({
        open: true,
        message: `Failed to ${selectedUnit ? 'update' : 'create'} unit: ${error.response?.data?.message || error.message}`,
        severity: 'error'
      });
    }
  };
  
  // Delete handlers
  const handleOpenDeleteDialog = (unit) => {
    setUnitToDelete(unit);
    setDeleteDialogOpen(true);
  };
  
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setUnitToDelete(null);
  };
  
  const handleDeleteUnit = async () => {
    try {
      await unitService.deleteUnit(unitToDelete.id);
      setSnackbar({
        open: true,
        message: 'Unit of measurement deleted successfully',
        severity: 'success'
      });
      handleCloseDeleteDialog();
      loadUnits();
    } catch (error) {
      console.error('Error deleting unit:', error);
      setSnackbar({
        open: true,
        message: `Failed to delete unit: ${error.response?.data?.message || error.message}`,
        severity: 'error'
      });
    }
  };
  
  // Snackbar close handler
  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };
  
  return (
    <Layout>
      <PageTitle title="Units" />
      <Box sx={{ width: '100%', p: 2 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            Units of Measurement
          </Typography>
          
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Unit
          </Button>
        </Box>
        
        {/* Units Table */}
        <Paper elevation={0} sx={{ 
          p: 0, 
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
        }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Abbreviation</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : units.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No units of measurement found
                    </TableCell>
                  </TableRow>
                ) : (
                  units.map((unit) => (
                    <TableRow key={unit.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <MeasureIcon sx={{ mr: 1, color: 'primary.main' }} />
                          {unit.name}
                        </Box>
                      </TableCell>
                      <TableCell>{unit.abbreviation}</TableCell>
                      <TableCell>{unit.description || '-'}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex' }}>
                          <Tooltip title="Edit">
                            <IconButton
                              color="info"
                              onClick={() => handleOpenDialog(unit)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              color="error"
                              onClick={() => handleOpenDeleteDialog(unit)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Pagination */}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalElements}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </Box>
      
      {/* Add/Edit Unit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedUnit ? 'Edit Unit of Measurement' : 'Add New Unit of Measurement'}
        </DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Unit Name"
              name="name"
              value={formValues.name}
              onChange={handleInputChange}
              margin="normal"
              required
              helperText="e.g., Kilogram, Liter, Piece"
            />
            
            <TextField
              fullWidth
              label="Abbreviation"
              name="abbreviation"
              value={formValues.abbreviation}
              onChange={handleInputChange}
              margin="normal"
              required
              helperText="e.g., kg, L, pc"
            />
            
            <TextField
              fullWidth
              label="Description"
              name="description"
              value={formValues.description}
              onChange={handleInputChange}
              margin="normal"
              multiline
              rows={3}
              helperText="Optional description of the unit"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={!formValues.name || !formValues.abbreviation}
          >
            {selectedUnit ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the unit <strong>{unitToDelete?.name}</strong>?
            This action cannot be undone.
          </Typography>
          <Typography variant="caption" color="error" sx={{ mt: 2, display: 'block' }}>
            Note: Units that are in use by products cannot be deleted. You must reassign products to a different unit first.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button 
            onClick={handleDeleteUnit} 
            variant="contained" 
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
};

export default UnitsPage; 